import { useState, useCallback, useMemo, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';

const API_URL = 'https://support-pilot-e6760c788c7c.herokuapp.com/auth/token';

interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  access_token: string;
  userId: number;
}

export function useAuth() {
  const [token, setToken] = useState<string | null>(() => {
    return Cookies.get('authToken') || null;
  });
  const [userId, setUserId] = useState<number | null>(() => {
    const storedUserId = Cookies.get('userId');
    return storedUserId ? parseInt(storedUserId, 10) : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiUrl = "https://support-pilot-e6760c788c7c.herokuapp.com"

  useEffect(() => {
    const storedToken = Cookies.get('authToken');
    const storedUserId = Cookies.get('userId');
    if (storedToken && storedUserId) {
      setToken(storedToken);
      setUserId(parseInt(storedUserId, 10));
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const data = new URLSearchParams({
        grant_type: 'password',
        username: credentials.username,
        password: credentials.password
      });
      
      const headers = {
        'accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
      };

      const response = await axios.post<LoginResponse>(`${apiUrl}/auth/token`, data, { headers });

      if (response.data.access_token && response.data.userId) {
        const newToken = response.data.access_token;
        const newUserId = response.data.userId;
        Cookies.set('authToken', newToken, { secure: true, sameSite: 'strict' });
        Cookies.set('userId', newUserId.toString(), { secure: true, sameSite: 'strict' });
        setToken(newToken);
        setUserId(newUserId);
        setIsLoading(false);
        return true;
      } else {
        throw new Error('Login failed: No access token or user ID received');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.message || error.message || 'Login failed');
      } else {
        setError('An unexpected error occurred');
      }
      setIsLoading(false);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    Cookies.remove('authToken');
    Cookies.remove('userId');
    setToken(null);
    setUserId(null);
  }, []);

  const auth = useMemo(() => ({
    token,
    userId,
    isLoading,
    error,
    login,
    logout,
    isAuthenticated: !!token && !!userId
  }), [token, userId, isLoading, error, login, logout]);

  return auth;
}